import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, IpsecTunnel } from '@netfoundry-ui/shared/model';
import {
    HateoasResourceOperation,
    PagedGetOption,
} from '@lagoshny/ngx-hateoas-client';
import { tap } from 'rxjs/operators';
import { HTTP_CLIENT } from './token.service';


@Injectable({ providedIn: 'root' })
export class IpsecTunnelService extends HateoasResourceOperation<IpsecTunnel> {
    apiUrl;
    lastPageCount = 0;
    lastTotalCount = 0;

    constructor(
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(IpsecTunnel);
        this.apiUrl = this.environment.frontdoorURL;
    }

    public getIpsecTunnels(frontdoorId: string, options?: PagedGetOption): Promise<IpsecTunnel[]> {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');

        let params = new HttpParams().set('sort', 'createdAt,DESC');
        if (options) {
            if (options.pageParams) {
                Object.keys(options.pageParams).forEach(key => {
                    const value = options.pageParams[key];
                    if (value !== undefined && value !== null) {
                        params = params.append(key, String(value));
                    }
                });
            }

            if (options.params) {
                Object.keys(options.params).forEach(key => {
                    const value = options.params[key];
                    if (value !== undefined && value !== null) {
                        params = params.append(key, String(value));
                    }
                });
            }
        }
        return this.http
            .get<any>(`${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels`, {
                headers,
                params,
            })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.page.totalPages;
                    this.lastTotalCount = val.page.totalElements;
                })
            )
            .toPromise();
    }

    public getIpsecTunnel(frontdoorId: string, id: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${id}`;
        return this.http.get(fullpath, {
            headers: { Accept: 'application/hal+json' },
        });
    }

    public create(model, frontdoorId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels`;
        return this.http.post(fullpath, model);
    }

    /**
     * Runs the create constraints server-side and returns the violations without creating
     * anything. Same path and body as create; the nf-validate header is what selects the dry run.
     *
     * Each violation carries a `path` such as `$.peerCidrs` and a `message`. The response is an
     * empty array when the request would be accepted.
     */
    public validateCreate(model, frontdoorId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels`;
        return this.http.post<{ path: string; label?: string; message: string }[]>(fullpath, model, {
            headers: new HttpHeaders().set('nf-validate', 'true'),
        });
    }

    public delete(frontdoorId: string, id: string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${id}`;
        return this.http.delete(fullpath, { headers });
    }

    public getTunnelConfig(frontdoorId: string, id: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${id}/tunnel-config`;
        return this.http.get(fullpath, { responseType: 'text' });
    }

    public getDeviceTypes(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/device-types`;
        return this.http.get<any[]>(fullpath);
    }

    public getMetrics(frontdoorId: string, tunnelId: string, window = '1h') {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${tunnelId}/metrics`;
        const params = new HttpParams().set('window', window);
        return this.http.get<any>(fullpath, { params });
    }

    public getBridgeHealth(frontdoorId: string, tunnelId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${tunnelId}/bridge-health`;
        return this.http.get<any>(fullpath);
    }

    // Health for every tunnel in the frontdoor, keyed by tunnel id. One request for a list however
    // many rows it shows; the template aggregates by tunnel server-side.
    public getBridgeHealthAll(frontdoorId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/bridge-health`;
        return this.http.get<Record<string, any>>(fullpath);
    }

    // `kind` filters by log source (charon, frontdoor-agent); omit it for every source. `search`
    // restricts to messages containing a word or phrase, applied by Elasticsearch rather than here
    // — the response is capped at `size`, so filtering client-side would only search that page.
    public getEvents(
        frontdoorId: string,
        tunnelId: string,
        window = '1h',
        kind: string | null = null,
        search: string | null = null,
        size = 500
    ) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${tunnelId}/events`;
        let params = new HttpParams().set('window', window).set('size', String(size));
        if (kind) {
            params = params.set('kind', kind);
        }
        if (search) {
            params = params.set('search', search);
        }
        return this.http.get<any>(fullpath, { params });
    }

    public getSampleConfig(frontdoorId: string, tunnelId: string) {
        const fullpath = `${this.apiUrl}/v2/${frontdoorId}/ipsec-tunnels/${tunnelId}/sample-config`;
        return this.http.get(fullpath, { responseType: 'text' });
    }

    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();
        return headers.append('Content-Type', 'application/json');
    }
}
